import { typeNameFor } from '@/fs/icons';
import { modifiedAt, sizeOf } from '@/fs/stat';
import { separator } from '@/shell/Menus/types';
import { useSettingsStore } from '@/settings/store';
import { CASCADE_ORIGIN, CASCADE_STEP, CASCADE_WRAP } from '@/wm/constants';
import { cascade, viewport } from '@/wm/placement';
import { selectTaskbarWindows, useWindowStore } from '@/wm/store';
import { formatLongDate } from '@/lib/time';
import type { FsNode } from '@/fs/types';
import type { MenuItem } from '@/shell/Menus/types';
import type { WindowState } from '@/wm/types';
import { msgbox, newMenu, searchCompanion } from './common';
import { fileMenu } from './fileMenus';

/**
 * The shell's own context menus, one builder per target: the desktop and its
 * icons, the taskbar, a task button, the Start button and the notification
 * area.
 *
 * The builders are called fresh on each right-click, so they can read live
 * state straight off the stores and hand back plain `MenuItem[]` — the same
 * shape the application menu bars use. What a file or a folder gets is in
 * `fileMenus`; the rows they share are in `common`.
 */

/* --------------------------------------------------------------------------
   Arranging desktop icons

   Sizes and timestamps come from `fs/stat`, which is where Explorer's Details
   view gets them too. Two views of the same folder disagreeing about which
   file is bigger would be worse than either answer alone, so neither of them
   owns the arithmetic.
   -------------------------------------------------------------------------- */

export type ArrangeKey = 'name' | 'size' | 'type' | 'modified';

/** Folders have no size, and sort below every file, as they do in Explorer. */
function sortSize(node: FsNode): number {
  return sizeOf(node) ?? -1;
}

function byName(a: FsNode, b: FsNode): number {
  return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
}

/** Comparator for one Arrange Icons By order. Ties fall back to the name. */
export function compareNodes(a: FsNode, b: FsNode, key: ArrangeKey): number {
  switch (key) {
    case 'size':
      return sortSize(a) - sortSize(b) || byName(a, b);
    case 'type':
      return typeNameFor(a).localeCompare(typeNameFor(b)) || byName(a, b);
    case 'modified':
      return modifiedAt(a).getTime() - modifiedAt(b).getTime() || byName(a, b);
    default:
      return byName(a, b);
  }
}

/* --------------------------------------------------------------------------
   The desktop
   -------------------------------------------------------------------------- */

export interface DesktopMenuOptions {
  /** Current sort order, or null while the icons sit in their authored order. */
  arrange: ArrangeKey | null;
  autoArrange: boolean;
  alignToGrid: boolean;
  onArrange(key: ArrangeKey): void;
  onToggleAutoArrange(): void;
  onToggleAlignToGrid(): void;
  onRefresh(): void;
}

export function desktopMenu(options: DesktopMenuOptions): MenuItem[] {
  const arrangeItem = (key: ArrangeKey, label: string): MenuItem => ({
    label,
    // `radio` carries the "one of these" semantics even when it is off, which
    // is what makes the row announce as a radio item rather than a command.
    radio: options.arrange === key,
    onSelect: () => options.onArrange(key),
  });

  return [
    {
      label: 'Arrange &Icons By',
      items: [
        arrangeItem('name', '&Name'),
        arrangeItem('size', 'Si&ze'),
        arrangeItem('type', '&Type'),
        arrangeItem('modified', '&Modified'),
        separator,
        {
          label: 'Auto &Arrange',
          checked: options.autoArrange,
          onSelect: options.onToggleAutoArrange,
        },
        {
          label: 'Align to &Grid',
          checked: options.alignToGrid,
          onSelect: options.onToggleAlignToGrid,
        },
      ],
    },
    { label: '&Refresh', onSelect: options.onRefresh },
    separator,
    { label: '&Paste', disabled: true },
    { label: 'Paste &Shortcut', disabled: true },
    separator,
    newMenu(),
    separator,
    {
      label: 'P&roperties',
      onSelect: () => {
        useWindowStore.getState().open('display');
      },
    },
  ];
}

/* --------------------------------------------------------------------------
   A desktop icon
   -------------------------------------------------------------------------- */

/**
 * `selection` is everything the command should act on, which is the whole
 * selection when the right-click landed inside one and just this icon when it
 * did not.
 *
 * Explore opens a second window with the folder tree showing, which is what
 * distinguished it from Open on a machine where both otherwise went to the
 * same place.
 */
export function desktopIconMenu(node: FsNode, selection: readonly FsNode[] = [node]): MenuItem[] {
  return fileMenu(node, {
    selection,
    onExplore: (path) => {
      useWindowStore.getState().open('explorer', { path, folders: 1 });
    },
  });
}

/* --------------------------------------------------------------------------
   Window arrangement, driven from the taskbar
   -------------------------------------------------------------------------- */

/** Windows that arranging applies to: on screen, and not an owned dialog. */
function arrangeable(): WindowState[] {
  return selectTaskbarWindows(useWindowStore.getState()).filter((w) => !w.minimized);
}

/** Windows fell out of maximized before being moved; ours have to as well. */
function unmaximize(win: WindowState): void {
  if (win.maximized) useWindowStore.getState().toggleMaximize(win.id);
}

function cascadeWindows(): void {
  const targets = arrangeable();
  if (targets.length === 0) return;

  const vp = viewport();
  // Every window ends up the same size, so the staircase has to be subtracted
  // from the desktop up front or the last one hangs off the bottom right.
  const steps = Math.min(targets.length, CASCADE_WRAP) - 1;
  const size = {
    w: Math.max(320, vp.w - CASCADE_ORIGIN.x - steps * CASCADE_STEP - 16),
    h: Math.max(220, vp.h - CASCADE_ORIGIN.y - steps * CASCADE_STEP - 16),
  };

  targets.forEach((win, index) => {
    unmaximize(win);
    useWindowStore.getState().setRect(win.id, cascade(index, size, vp));
  });
  // Oldest at the back, newest on top and active — the way the stack reads.
  useWindowStore.getState().focus(targets[targets.length - 1].id);
}

/**
 * Tile into a grid. Two or three windows get plain bands — stacked for
 * Horizontally, side by side for Vertically, which is the arrangement people
 * actually remember — and beyond that Windows falls back to a grid, as we do.
 */
function tileWindows(direction: 'h' | 'v'): void {
  const targets = arrangeable();
  const count = targets.length;
  if (count === 0) return;

  const vp = viewport();
  const major = count <= 3 ? count : Math.ceil(Math.sqrt(count));
  const rows = direction === 'h' ? major : Math.ceil(count / major);
  const cols = direction === 'h' ? Math.ceil(count / major) : major;

  const cellH = Math.floor(vp.h / rows);
  let placed = 0;

  for (let row = 0; row < rows && placed < count; row += 1) {
    // A short final row spreads across the full width rather than leaving a
    // gap, so the tiling always covers the whole desktop.
    const inRow = Math.min(cols, count - placed);
    const cellW = Math.floor(vp.w / inRow);

    for (let col = 0; col < inRow; col += 1) {
      const win = targets[placed + col];
      unmaximize(win);
      useWindowStore.getState().setRect(win.id, {
        x: col * cellW,
        y: row * cellH,
        w: col === inRow - 1 ? vp.w - col * cellW : cellW,
        h: row === rows - 1 ? vp.h - row * cellH : cellH,
      });
    }
    placed += inRow;
  }
}

/** Minimises everything, or puts it all back if the desktop is already bare. */
function toggleShowDesktop(): void {
  const tasks = selectTaskbarWindows(useWindowStore.getState());
  const visible = tasks.filter((w) => !w.minimized);

  if (visible.length > 0) {
    for (const win of visible) useWindowStore.getState().minimize(win.id);
    return;
  }
  // Restoring in creation order leaves the newest window focused on top.
  for (const win of tasks) useWindowStore.getState().focus(win.id);
}

/* --------------------------------------------------------------------------
   The taskbar
   -------------------------------------------------------------------------- */

/**
 * "Lock the Taskbar" is a real setting in XP, but this taskbar has nothing to
 * unlock: it cannot be dragged to another edge, resized, or given toolbars. The
 * check mark is still remembered so the menu behaves the way it looks, and it
 * lives here rather than in React state because the menu is rebuilt from
 * scratch on every right-click and nothing else needs to re-render.
 */
let taskbarLocked = true;

/** Decorative: the toolbars XP could dock, none of which exist here. */
const TOOLBARS: MenuItem[] = [
  { label: '&Address', checked: false, disabled: true },
  { label: '&Links', checked: false, disabled: true },
  { label: 'Language &bar', checked: false, disabled: true },
  { label: '&Desktop', checked: false, disabled: true },
  { label: '&Quick Launch', checked: true, disabled: true },
  separator,
  { label: '&New Toolbar...', disabled: true },
];

/** Reached from the taskbar, the notification area and the Start button. */
function taskbarProperties(): void {
  msgbox(
    {
      title: 'Taskbar and Start Menu Properties',
      icon: 'info',
      text: 'Taskbar and Start Menu Properties are not available on this computer.',
      detail:
        'The wallpaper and the colour scheme are on offer though — right-click ' +
        'the desktop and choose Properties.',
    },
    190,
  );
}

export function taskbarMenu(): MenuItem[] {
  const open = selectTaskbarWindows(useWindowStore.getState());
  // An empty desktop is already showing, but "Show Open Windows" with nothing
  // to show would be nonsense — so the flip needs windows that are down, not
  // merely an absence of windows that are up.
  const showDesktop = open.length === 0 || open.some((w) => !w.minimized);

  return [
    { label: '&Toolbars', items: TOOLBARS },
    separator,
    { label: 'Casca&de Windows', onSelect: cascadeWindows },
    { label: 'Tile Windows &Horizontally', onSelect: () => tileWindows('h') },
    { label: 'Tile Windows V&ertically', onSelect: () => tileWindows('v') },
    // XP swapped the wording once the desktop was showing, and swapped it back.
    {
      label: showDesktop ? '&Show the Desktop' : 'Show &Open Windows',
      onSelect: toggleShowDesktop,
    },
    separator,
    {
      label: 'Tas&k Manager',
      onSelect: () => {
        useWindowStore.getState().open('taskmgr');
      },
    },
    separator,
    {
      label: '&Lock the Taskbar',
      checked: taskbarLocked,
      onSelect: () => {
        taskbarLocked = !taskbarLocked;
      },
    },
    { label: 'P&roperties', onSelect: taskbarProperties },
  ];
}

/* --------------------------------------------------------------------------
   The notification area

   XP gave the clock and the icons beside it the taskbar's own menu with two
   rows added — the notification area is part of the taskbar, and right-clicking
   it was another way to reach Cascade Windows. The two extra rows go where the
   real ones did: above Lock the Taskbar, at the end.
   -------------------------------------------------------------------------- */

export function trayMenu(): MenuItem[] {
  const base = taskbarMenu();
  // Splice rather than rebuild, so the two menus can never drift apart.
  const lockAt = base.findIndex((item) => item.label === '&Lock the Taskbar');
  const extras: MenuItem[] = [
    { label: 'Customi&ze Notifications...', disabled: true },
    { label: 'Ad&just Date/Time', onSelect: adjustDateTime },
    separator,
  ];

  return lockAt === -1 ? [...base, ...extras] : [
    ...base.slice(0, lockAt),
    ...extras,
    ...base.slice(lockAt),
  ];
}

/**
 * The clock is the visitor's own, read straight off their machine, so there is
 * nothing here to set — but saying so is more use than a greyed row, and the
 * date is worth showing while we have their attention.
 */
function adjustDateTime(): void {
  msgbox(
    {
      title: 'Date and Time Properties',
      icon: 'info',
      text: formatLongDate(new Date()),
      detail:
        'The clock in the corner is your own computer’s. Windows has no clock of ' +
        'its own in here, so there is nothing for this page to set.',
    },
    190,
  );
}

/** The speaker icon: XP's two rows, plus the mute this one really does have. */
export function volumeMenu(): MenuItem[] {
  const { soundEnabled, setSoundEnabled } = useSettingsStore.getState();

  return [
    { label: 'Open &Volume Control', disabled: true },
    { label: '&Adjust Audio Properties', disabled: true },
    separator,
    {
      label: '&Mute',
      checked: !soundEnabled,
      onSelect: () => setSoundEnabled(!soundEnabled),
    },
  ];
}

/* --------------------------------------------------------------------------
   The Start button
   -------------------------------------------------------------------------- */

/**
 * Open and Explore both went to the folder the Start menu was made of. This
 * one is not made of a folder — it is assembled from the installed programs —
 * so the menu says so rather than opening the wrong place.
 */
function noStartMenuFolder(): void {
  msgbox(
    {
      title: 'Start Menu',
      icon: 'info',
      text: 'This Start menu has no folder behind it.',
      detail:
        'Its shortcuts are built from the programs that are installed, so there is ' +
        'nothing on the disk to open. All Programs has the same list.',
    },
    190,
  );
}

export function startButtonMenu(): MenuItem[] {
  return [
    { label: '&Open', onSelect: noStartMenuFolder },
    { label: '&Explore', onSelect: noStartMenuFolder },
    separator,
    { label: 'Open All &Users', disabled: true },
    { label: 'Explore All Us&ers', disabled: true },
    separator,
    { label: 'Sea&rch...', onSelect: searchCompanion },
    separator,
    { label: 'P&roperties', onSelect: taskbarProperties },
  ];
}

/* --------------------------------------------------------------------------
   A window's System menu

   Raised by the caption icon, by a right-click on the caption, and by a
   right-click on the window's task button — three doors into the same menu,
   which is why it takes a window rather than knowing where it was asked from.
   -------------------------------------------------------------------------- */

/**
 * `win` is a snapshot taken when the menu was raised, which is exactly what the
 * greying should reflect: nothing else can move that window while the popup has
 * the pointer and the keyboard.
 */
export function systemMenu(win: WindowState): MenuItem[] {
  const { id } = win;

  return [
    {
      label: '&Restore',
      disabled: !win.minimized && !win.maximized,
      onSelect: () => {
        const wm = useWindowStore.getState();
        // `focus` clears the minimised flag; a maximized window then still owes
        // us its restore rectangle, so both steps can apply at once.
        if (win.minimized) wm.focus(id);
        if (win.maximized) wm.toggleMaximize(id);
        wm.focus(id);
      },
    },
    // Dragging a window by the keyboard was never implemented here, and a
    // command that silently does nothing is worse than one that is plainly off.
    { label: '&Move', disabled: true },
    { label: '&Size', disabled: true },
    {
      label: 'Mi&nimize',
      disabled: win.minimized || win.dialog,
      onSelect: () => useWindowStore.getState().minimize(id),
    },
    {
      label: 'Ma&ximize',
      disabled: !win.maximizable || win.maximized,
      onSelect: () => {
        const wm = useWindowStore.getState();
        if (win.minimized) wm.focus(id);
        wm.toggleMaximize(id);
      },
    },
    separator,
    {
      label: '&Close',
      shortcut: 'Alt+F4',
      onSelect: () => useWindowStore.getState().close(id),
    },
  ];
}
